import { Navigate, Outlet } from "react-router-dom";

import { API_ADMIN_PATHS, CORPORATE_BACKOFFICE_PATHS, HASURA_ROLE, MADERA_BACKOFFICE_PATHS } from "constants/constants";
import { isTokenAvailable } from "utilities/storageUtilities";
import { getTokenDefaultRole, isApiAdministrator } from "utilities/utilities";

/**
 * Gate for the API Administrator portal (OS-2557).
 *
 * The role is read from the signed JWT rather than CorporateContext so the decision is synchronous
 * on first render — the portal's own profile query has not resolved yet at this point.
 *
 * A valid token belonging to any other role is sent to the CBO's own API Key Management page
 * rather than the portal login, so a Corporate Admin who follows a portal link is not logged out.
 */
export const ProtectedApiAdminRoute = ({
    redirectPath = `/${API_ADMIN_PATHS.ROOT_PATH}/${API_ADMIN_PATHS.LOGIN}`,
}: {
    redirectPath?: string;
}) => {
    const token = isTokenAvailable();

    if (!token) {
        return <Navigate to={redirectPath} replace />;
    }

    if (!isApiAdministrator()) {
        // Send them where they can actually work, not to one fixed page: the CBO key page reads
        // "API access is not available for your organization" for every corporate role except
        // Administrator (a false reason — they simply lack the permission), and it renders an
        // unfiltered cross-Corporation key list for Madera staff.
        const defaultRole = getTokenDefaultRole();

        if (defaultRole === HASURA_ROLE.OSORNO_ADMINISTRATOR) {
            return (
                <Navigate
                    to={`/${MADERA_BACKOFFICE_PATHS.ROOT_PATH}/${MADERA_BACKOFFICE_PATHS.SETUP_DASHBOARD.ROOT}`}
                    replace
                />
            );
        }

        if (defaultRole === HASURA_ROLE.ADMINISTRATOR) {
            return (
                <Navigate
                    to={`/${CORPORATE_BACKOFFICE_PATHS.ROOT_PATH}/${CORPORATE_BACKOFFICE_PATHS.API_KEY_MANAGEMENT}`}
                    replace
                />
            );
        }

        // Same landing the CBO catch-all uses, so any other corporate role ends up on a page it
        // can read rather than a permission-gated one.
        return (
            <Navigate
                to={`/${CORPORATE_BACKOFFICE_PATHS.ROOT_PATH}/${CORPORATE_BACKOFFICE_PATHS.LOCATIONS.ROOT}/0`}
                replace
            />
        );
    }

    return <Outlet />;
};
